<script>
import { mapState, mapActions } from 'vuex';
import validationRules from '../../mixins/validationRules';

export default {
  name: 'ReminderListsTableFilter',
  mixins: [
    validationRules,
  ],
  data() {
    return {
      chosenDomains: [],
      showMessages: 'all',
      showCancelled: 'all',
      search: undefined,
      show: false,
      selectOptions: [
        { text: 'All', value: 'all' },
        { text: 'None', value: 'none' },
        { text: 'Only', value: 'only' },
      ],
      menuProps: {
        closeOnClick: false,
        closeOnContentClick: false,
        maxHeight: 250,
      },
    };
  },
  computed: {
    ...mapState({
      filters: state => state.reminders.lists.filters,
      domains: state => state.reminders.domains,
      loading: state => state.reminders.lists.list.loading,
    }),
    hasFilter() {
      return this.filters.chosenDomains.length ||
        this.filters.showMessages !== 'all' ||
        this.filters.showCancelled !== 'all' ||
        this.filters.search;
    },
  },
  methods: {
    ...mapActions({
      getDomains: 'reminders/loadDomains',
      updateFilters: 'reminders/updateListFilters',
    }),
    close() {
      this.show = false;
    },
    removeDomain(item) {
      const index = this.chosenDomains.findIndex(domain => domain === item.id);
      if (index >= 0) {
        this.chosenDomains.splice(index, 1);
      }
    },
    applyFilters() {
      const filters = {
        chosenDomains: this.chosenDomains,
        showMessages: this.showMessages,
        showCancelled: this.showCancelled,
        search: this.search,
      };
      this.updateFilters(filters);
      this.close();
    },
    clearFilters() {
      const filters = {
        chosenDomains: [],
        showMessages: 'all',
        showCancelled: 'all',
        search: undefined,
      };
      this.chosenDomains = [];
      this.showMessages = 'all';
      this.showCancelled = 'all';
      this.search = undefined;
      this.updateFilters(filters);
      this.close();
    },
  },
  created() {
    if (!this.domains.items.length && !this.domains.loading) {
      this.getDomains();
    }
    this.chosenDomains = [...this.filters.chosenDomains];
    this.showMessages = this.filters.showMessages;
    this.showCancelled = this.filters.showCancelled;
    this.search = this.filters.search;
  },
};
</script>

<template>
  <v-flex xs12 reminder-lists__table-filter caption>
    <v-menu v-model="show" class="d-inline" :close-on-click="false" :close-on-content-click="false">
      <template v-slot:activator="{ on }">
        <v-btn v-on="on" class="reminder-lists__table-filter-icon d-inline ma-0" small icon>
          <v-icon :class="{ 'orange--text': hasFilter, 'grey--text': !hasFilter }" size="24">filter_alt</v-icon>
        </v-btn>
      </template>
      <v-card width="800px">
        <v-card-title>
          <h3>Table Filters</h3>
          <v-spacer/>
          <v-btn class="ma-0" small icon @click="close"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 mb-3>
              <v-autocomplete
                v-model="chosenDomains"
                :items="domains.items"
                :loading="domains.loading"
                :disabled="domains.loading"
                :menu-props="menuProps"
                label="Choose Domains"
                item-value="id"
                item-text="name"
                dense
                multiple
                clearable
              >
                <template v-slot:selection="data">
                  <v-chip
                    :selected="data.selected"
                    class="chip--select-multi"
                    close
                    small
                    @input="removeDomain(data.item)"
                  >
                    {{ data.item.shortName }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="showMessages"
                :items="selectOptions"
                label="Payment Remarks"
              />
            </v-flex>
            <v-flex xs12>
              <v-select
                v-model="showCancelled"
                :items="selectOptions"
                label="Cancelled Bookings"
              />
            </v-flex>
            <v-flex xs12>
              <v-text-field
                v-model="search"
                maxlength="30"
                label="Search in customers and/or suppliers"
                counter
                clearable
              />
            </v-flex>
          </v-layout>
          <v-layout mt-4>
            <v-spacer/>
            <v-btn color="error" @click="clearFilters">Clear Filters</v-btn>
            <v-btn color="info" @click="applyFilters">Apply Filters</v-btn>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-menu>
    <div class="d-inline">
      <span class="font-weight-bold">Cancelled: </span>
      <span v-if="filters.showCancelled === 'only'" class="red--text">Only</span>
      <span v-else-if="filters.showCancelled === 'all'" class="green--text">All</span>
      <span v-else class="red--text">None</span>
    </div>
    <div class="d-inline ml-2">
      <span class="font-weight-bold">Payment Remarks: </span>
      <span v-if="filters.showMessages === 'only'" class="red--text">Only</span>
      <span v-else-if="filters.showMessages === 'all'" class="green--text">All</span>
      <span v-else class="red--text">None</span>
    </div>
    <div class="d-inline ml-2">
      <span class="font-weight-bold">Domains: </span>
      <span v-if="!filters.chosenDomains.length" class="green--text">All</span>
      <span v-else class="red--text">{{ filters.chosenDomains.length }}</span>
    </div>
    <div class="d-inline ml-2">
      <span class="font-weight-bold">Search: </span>
      <span v-if="!filters.search" class="grey--text">-</span>
      <span v-else class="red--text">{{ filters.search }}</span>
    </div>
    <v-btn
      :class="{ 'icon-spin': loading }"
      :disabled="loading"
      class="reminder-lists__table-filter-refresh"
      icon
      @click="$emit('refresh')"
    >
      <v-icon>refresh</v-icon>
    </v-btn>
  </v-flex>
</template>

<style lang="scss">
.reminder-lists__table-filter {
  position: relative;
  height: 20px;
  overflow: visible;
  z-index: 1;

  &-icon {
    width: 24px;
  }

  &-refresh {
    position: absolute !important;
    top: -10px;
    right: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Reminders/ReminderListsTableFilter.vue